/**
 * THE SCENARIO SET A BLOCK BELONGS TO, FOR THE CLIPBOARD.
 *
 * A scenario set is one leader and every block following it. It lives entirely inside one task —
 * `ScenarioLinkMenu` searches the current task's block list and nothing else — so a block that
 * leaves its task leaves its set behind. That is the whole reason this file exists: copying one
 * member has to be able to bring the others, because the destination cannot find them.
 *
 * ## Membership is read from the LINKS, not from eligibility
 *
 * `canLeadScenarios` answers "could this block lead?" and is the right test for the link MENU,
 * which offers candidates. Here the question is "what is actually linked to what", so the only
 * thing consulted is `followsBlockId`. A leader whose variants have since dropped below two is
 * still the leader of its followers, and a set that has become ineligible is exactly the set a
 * teacher is most likely to be moving somewhere else.
 *
 * ## No chaining (§2.4), which is what keeps this a flat walk
 *
 * A block that follows something cannot itself be followed, so a set is one level deep: find the
 * leader, then find everyone pointing at it. If chaining ever arrives this becomes a graph walk
 * and the return type grows an ordering.
 */
import type { Block } from '@/types/experience';

export interface ScenarioSet {
  /** The block every member points at. Always present — a set with no leader is not a set. */
  leaderId: string;
  /** Every block following the leader, in task order. Never includes the leader. */
  followerIds: string[];
}

/** What `block.data` carries that matters here. Narrow, because `Block['data']` is a union. */
function followsOf(block: Block): string | undefined {
  return (block.data as { followsBlockId?: string } | undefined)?.followsBlockId;
}

/**
 * The set `blockId` belongs to, or `null` when it belongs to none.
 *
 * Works from either end: pass a follower and the leader is resolved from its `followsBlockId`,
 * pass a leader and its followers are collected. A follower whose leader is missing from `blocks`
 * returns `null` rather than a one-sided set — there is nothing to bring along, so the caller has
 * nothing to offer.
 */
export function scenarioSetOf(blockId: string, blocks: Block[]): ScenarioSet | null {
  const self = blocks.find(b => b.id === blockId);
  if (!self) return null;

  const leaderId = followsOf(self) ?? self.id;
  if (!blocks.some(b => b.id === leaderId)) return null;

  const followerIds = blocks.filter(b => followsOf(b) === leaderId).map(b => b.id);
  // A block that follows nothing and is followed by nothing is not in a set. Checked after the
  // collect rather than before, because "am I a leader" is exactly "does anyone point at me".
  if (followerIds.length === 0) return null;

  return { leaderId, followerIds };
}

/**
 * The set's members IN THE ORDER THEY APPEAR IN THE TASK.
 *
 * Not subject-first. A pasted set has to arrive looking like the set it came from — a scenario
 * landing underneath the block that follows it reads as broken, and the checklist that offers
 * the members has to list them the way the teacher already has them on screen. The subject is
 * carried separately, as `ClipboardBlocks.subjectId`, because it is an identity rather than a
 * position and encoding it as one was what forced the two to disagree.
 */
export function scenarioSetMembers(blockId: string, blocks: Block[]): string[] | null {
  const set = scenarioSetOf(blockId, blocks);
  if (!set) return null;
  const inSet = new Set([set.leaderId, ...set.followerIds]);
  return blocks.filter(b => inSet.has(b.id)).map(b => b.id);
}
